/**
 * No `timeout`: `PositionOptions.timeout` is a TIMEOUT error on that acquire,
 * and Safari aborts a live watch when a timed request beside it fails
 * (docs/lessons_learned/20261009_geolocation_hint_and_timeout.md). The 12 s
 * app timer in `runPinCapture` owns the wait instead, and a 60 s `maximumAge`
 * lets CoreLocation hand over its fused last-known as the first callback —
 * which is what makes "I'm standing on the pin" answer under a canopy.
 */
export const GPS_OPTS = {enableHighAccuracy: true, maximumAge: 60_000}
export const WATCH_MS = 12_000
export const GOOD_ENOUGH_M = 25

export function coordsPayload(coords) {
  return {
    ok: true,
    lat: coords.latitude,
    lng: coords.longitude,
    accuracy: Number.isFinite(coords.accuracy) ? coords.accuracy : null,
    altitude: Number.isFinite(coords.altitude) ? coords.altitude : null,
    altitude_accuracy: Number.isFinite(coords.altitudeAccuracy)
      ? coords.altitudeAccuracy
      : null,
  }
}

export function betterCoords(a, b) {
  if (!b) return a
  if (!a) return b
  const aa = Number.isFinite(a.accuracy) ? a.accuracy : Infinity
  const ba = Number.isFinite(b.accuracy) ? b.accuracy : Infinity
  return ba < aa ? b : a
}

export function isGoodEnough(coords, limit = GOOD_ENOUGH_M) {
  return Number.isFinite(coords?.accuracy) && coords.accuracy <= limit
}

export function runPinCapture(geo, hooks, opts = {}) {
  const push = hooks.push
  const onSettled = hooks.onSettled || (() => {})
  const watchMs = opts.watchMs ?? WATCH_MS
  const gpsOpts = opts.gpsOpts ?? GPS_OPTS
  const goodEnough = opts.goodEnoughM ?? GOOD_ENOUGH_M
  const setT = opts.setTimeout ?? setTimeout
  const clearT = opts.clearTimeout ?? clearTimeout

  if (!geo || typeof geo.watchPosition !== "function") {
    push({ok: false, error: "unsupported"})
    onSettled()
    return () => {}
  }

  let best = null
  let done = false
  let watchId = null
  let timer = null

  const finish = (err, silent) => {
    if (done) return
    done = true
    if (watchId != null && typeof geo.clearWatch === "function") geo.clearWatch(watchId)
    if (timer != null) clearT(timer)
    if (!silent) {
      if (best) push(coordsPayload(best))
      else push({ok: false, error: String(err?.code ?? err ?? "timeout")})
      onSettled()
    }
  }

  watchId = geo.watchPosition(
    (pos) => {
      best = betterCoords(best, pos.coords)
      if (isGoodEnough(best, goodEnough)) finish()
    },
    (err) => {
      if (err && err.code === 1) finish(err)
    },
    gpsOpts
  )
  timer = setT(() => finish(), watchMs)
  return () => finish(null, true)
}

export const PIN_CAPTURE_LABEL = "I’m standing on the pin"

export const pinCaptureHook = {
  mounted() {
    this._stop = null
    this.el.addEventListener("click", () => {
      if (this._stop) return
      this.el.disabled = true
      this.el.textContent = "Waiting for GPS…"
      this._stop = runPinCapture(navigator.geolocation, {
        push: (payload) => this.pushEvent("capture_pin", payload),
        onSettled: () => {
          this.el.disabled = false
          this.el.textContent = PIN_CAPTURE_LABEL
          this._stop = null
        },
      })
    })
  },
  destroyed() {
    if (this._stop) this._stop()
  },
}
